import React, { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import BlueButton from "../../../Component/Common/BlueButton";
import { useNavigate } from "react-router-dom";

export default function AddAgentForm() {
  const navigate = useNavigate();
  const [loadingPin, setLoadingPin] = useState(false);

  const [form, setForm] = useState({
    // Personal
    first_name: "",
    middle_name: "",
    last_name: "",
    phone_no: "",
    official_email: "",
    position: "",

    // Address
    address1: "",
    address2: "",
    pincode: "",
    state: "",
    district: "",
    taluka: "",
    location: "",

    // Business
    business_name: "",
    owner_first: "",
    owner_middle: "",
    owner_last: "",
    owner_contact: "",
    owner_email: "",
    pan_no: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === "pincode" && value.length === 6) {
      fetchPinDetails(value);
    }

    validateField(name, value);
  };

  const fetchPinDetails = async (pincode) => {
    try {
      setLoadingPin(true);
      const res = await axios.get(
        `https://goelectronix.in/public/api/v1/pincode/${pincode}`
      );

      if (res.data?.success) {
        setForm((prev) => ({
          ...prev,
          state: res.data.state,
          district: res.data.district,
          taluka: res.data.taluk || "",
        }));
        setErrors((prev) => ({ ...prev, pincode: "" }));
      } else {
        setErrors((prev) => ({ ...prev, pincode: "Invalid pincode" }));
        Swal.fire("Invalid Pincode", "Enter a correct pincode.", "error");
      }
    } catch {
      Swal.fire("Error", "Unable to fetch pincode details.", "error");
    } finally {
      setLoadingPin(false);
    }
  };

  const validateField = (name, value) => {
    let error = "";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[6-9]\d{9}$/;
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

    switch (name) {
      case "first_name":
      case "last_name":
      case "position":
      case "address1":
      case "business_name":
      case "owner_first":
      case "owner_last":
        if (!value.trim()) error = "This field is required";
        break;

      case "phone_no":
      case "owner_contact":
        if (!value.trim()) error = "This field is required";
        else if (!phoneRegex.test(value)) error = "Invalid 10-digit number";
        break;

      case "official_email":
      case "owner_email":
        if (!value.trim()) error = "This field is required";
        else if (!emailRegex.test(value)) error = "Invalid email address";
        break;

      case "pincode":
        if (!value.trim()) error = "This field is required";
        else if (value.length !== 6) error = "Pincode must be 6 digits";
        break;

      case "pan_no":
        if (!value.trim()) error = "This field is required";
        else if (!panRegex.test(value)) error = "Invalid PAN format (e.g., ABCDE1234F)";
        break;

      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
    return error === "";
  };

  const validateForm = () => {
    let valid = true;
    Object.keys(form).forEach((key) => {
      const isFieldValid = validateField(key, form[key]);
      if (!isFieldValid) valid = false;
    });
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire("Error", "Please fix validation errors", "error");
      return;
    }

    try {
      await axios.post("https://your-api.com/agent", form);
      Swal.fire("Success", "Agent added successfully!", "success").then(() =>
        navigate("/agents")
      );
    } catch (err) {
      Swal.fire("Error", "Failed to save agent.", "error");
    }
  };

  const renderInput = (label, name, type = "text", readOnly = false) => (
    <div>
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      <input
        type={type}
        name={name}
        value={form[name]}
        required
        onChange={handleChange}
        readOnly={readOnly}
        maxLength={name === "pincode" ? 6 : undefined}
        className={`mt-1 border rounded-lg px-3 py-2 text-sm w-full ${
          readOnly ? "bg-gray-100" : ""
        }`}
      />
      {errors[name] && <p className="text-xs text-red-500 mt-1">{errors[name]}</p>}
      {loadingPin && name === "pincode" && (
        <p className="text-xs text-blue-600 mt-1">Fetching...</p>
      )}
    </div>
  );

  return (
    <div className="mx-auto bg-white shadow-md rounded-2xl p-4 mt-2 mb-10">
      <h2 className="text-2xl font-semibold text-primary mb-4">Add New Agent</h2>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Personal Info */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">
            Personal Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {renderInput("First Name", "first_name")}
            {renderInput("Middle Name", "middle_name")}
            {renderInput("Last Name", "last_name")}
            {renderInput("Phone Number", "phone_no")}
            {renderInput("Official Email", "official_email", "email")}
            {renderInput("Position", "position")}
          </div>
        </div>

        {/* Address Info */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Address Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {renderInput("Address Line 1", "address1")}
            {renderInput("Address Line 2", "address2")}
            {renderInput("Pincode", "pincode")}
            {renderInput("State", "state", "text", true)}
            {renderInput("District", "district", "text", true)}
            {renderInput("Taluka", "taluka", "text", true)}
            {renderInput("Location", "location")}
          </div>
        </div>

        {/* Business Info */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Business Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {renderInput("Business Name", "business_name")}
            {renderInput("Owner First Name", "owner_first")}
            {renderInput("Owner Middle Name", "owner_middle")}
            {renderInput("Owner Last Name", "owner_last")}
            {renderInput("Owner Contact Number", "owner_contact")}
            {renderInput("Owner Email ID", "owner_email", "email")}
            {renderInput("PAN Number", "pan_no")}
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-3">
          <BlueButton type="button" onClick={() => navigate("/agents")}>
            Cancel
          </BlueButton>
          <BlueButton type="submit">Save Agent</BlueButton>
        </div>
      </form>
    </div>
  );
}


// import React, { useState } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import BlueButton from "../../../Component/BlueButton";
// import { useNavigate } from "react-router-dom";

// export default function AddAgentForm() {
//   const navigate = useNavigate();

//   const [loadingPin, setLoadingPin] = useState(false);

//   const [form, setForm] = useState({
//     // Personal
//     first_name: "",
//     middle_name: "",
//     last_name: "",
//     phone_no: "",
//     official_email: "",
//     position: "",

//     // Address
//     address1: "",
//     address2: "",
//     pincode: "",
//     state: "",
//     district: "",
//     taluka: "",
//     location: "",

//     // Business
//     business_name: "",
//     owner_first: "",
//     owner_middle: "",
//     owner_last: "",
//     owner_contact: "",
//     owner_email: "",
//     pan_no: "",
//   });

//   const handleChange = (e) => {
//     const { name, value } = e.target;

//     setForm((prev) => ({
//       ...prev,
//       [name]: value,
//     }));

//     if (name === "pincode" && value.length === 6) {
//       fetchPinDetails(value);
//     }
//   };

//   const fetchPinDetails = async (pincode) => {
//     try {
//       setLoadingPin(true);
//       const res = await axios.get(
//         `https://goelectronix.in/public/api/v1/pincode/${pincode}`
//       );

//       if (res.data?.success) {
//         setForm((prev) => ({
//           ...prev,
//           state: res.data.state,
//           district: res.data.district,
//           taluka: res.data.taluk || "",
//         }));
//       } else {
//         Swal.fire("Invalid Pincode", "Enter a correct pincode.", "error");
//       }
//     } catch {
//       Swal.fire("Error", "Unable to fetch pincode details.", "error");
//     } finally {
//       setLoadingPin(false);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     try {
//       await axios.post("https://your-api.com/agent", form);
//       Swal.fire("Success", "Agent added successfully!", "success").then(() =>
//         navigate("/agents")
//       );
//     } catch (err) {
//       Swal.fire("Error", "Failed to save agent.", "error");
//     }
//   };

//   return (
//     <div className="mx-auto bg-white shadow-md rounded-2xl p-2 mt-2 mb-10">
//       <h2 className="text-2xl font-semibold text-primary mb-4">
//         Add New Agent
//       </h2>

//       <form onSubmit={handleSubmit} className="space-y-8">

//         {/* PERSONAL INFORMATION */}
//         <div className="">
//           <h3 className="text-lg font-semibold text-slate-800 mb-2">
//             Personal Information
//           </h3>

//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

//             {/* First Name */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Authorised Person First Name
//               </label>
//               <input
//                 name="first_name"
//                 value={form.first_name}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//             {/* Middle Name */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Middle Name
//               </label>
//               <input
//                 name="middle_name"
//                 value={form.middle_name}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//             {/* Last Name */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Last Name
//               </label>
//               <input
//                 name="last_name"
//                 value={form.last_name}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//             {/* Phone */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Phone Number
//               </label>
//               <input
//                 name="phone_no"
//                 value={form.phone_no}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//             {/* Email */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Official Email
//               </label>
//               <input
//                 name="official_email"
//                 value={form.official_email}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//             {/* Position */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Position
//               </label>
//               <input
//                 name="position"
//                 value={form.position}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//           </div>
//         </div>

//         {/* ADDRESS SECTION */}
//         <div>
//           <h3 className="text-lg font-semibold text-slate-800 mb-2">
//             Address Details
//           </h3>

//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Address Line 1
//               </label>
//               <input
//                 name="address1"
//                 value={form.address1}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Address Line 2
//               </label>
//               <input
//                 name="address2"
//                 value={form.address2}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Pincode
//               </label>
//               <input
//                 name="pincode"
//                 value={form.pincode}
//                 maxLength={6}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//               {loadingPin && (
//                 <p className="text-xs text-blue-600 mt-1">Fetching...</p>
//               )}
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 State
//               </label>
//               <input
//                 name="state"
//                 readOnly
//                 value={form.state}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full bg-gray-100"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 District
//               </label>
//               <input
//                 name="district"
//                 readOnly
//                 value={form.district}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full bg-gray-100"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Taluka
//               </label>
//               <input
//                 name="taluka"
//                 readOnly
//                 value={form.taluka}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full bg-gray-100"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Location
//               </label>
//               <input
//                 name="location"
//                 value={form.location}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//           </div>
//         </div>

//         {/* BUSINESS DETAILS */}
//         <div>
//           <h3 className="text-lg font-semibold text-slate-800 mb-2">
//             Business Details
//           </h3>

//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Business Name
//               </label>
//               <input
//                 name="business_name"
//                 value={form.business_name}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Owner First Name
//               </label>
//               <input
//                 name="owner_first"
//                 value={form.owner_first}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Owner Middle Name
//               </label>
//               <input
//                 name="owner_middle"
//                 value={form.owner_middle}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Owner Last Name
//               </label>
//               <input
//                 name="owner_last"
//                 value={form.owner_last}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Owner Contact Number
//               </label>
//               <input
//                 name="owner_contact"
//                 value={form.owner_contact}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Owner Email ID
//               </label>
//               <input
//                 name="owner_email"
//                 value={form.owner_email}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 PAN Number
//               </label>
//               <input
//                 name="pan_no"
//                 value={form.pan_no}
//                 onChange={handleChange}
//                 className="mt-1 border rounded-lg px-3 py-2 text-sm w-full"
//               />
//             </div>

//           </div>
//         </div>

//         {/* ACTION BUTTONS */}
//         <div className="flex justify-end gap-3">
//           <BlueButton type="button" onClick={() => navigate("/agents")}>
//             Cancel
//           </BlueButton>
//           <BlueButton type="submit">Save Agent</BlueButton>
//         </div>

//       </form>
//     </div>
//   );
// }


// import React, { useState } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import BlueButton from "./BlueButton";
// import { useNavigate } from "react-router-dom";

// export default function AddAgentForm() {
//   const navigate = useNavigate();

//   const [loadingPin, setLoadingPin] = useState(false);

//   const [form, setForm] = useState({
//     // Personal
//     first_name: "",
//     middle_name: "",
//     last_name: "",
//     phone_no: "",
//     official_email: "",
//     position: "",

//     // Address
//     address1: "",
//     address2: "",
//     pincode: "",
//     state: "",
//     district: "",
//     taluka: "",
//     location: "",

//     // Business
//     business_name: "",
//     owner_first: "",
//     owner_middle: "",
//     owner_last: "",
//     owner_contact: "",
//     owner_email: "",

//     pan_no: "",
//   });

//   const handleChange = (e) => {
//     const { name, value } = e.target;

//     setForm((prev) => ({
//       ...prev,
//       [name]: value,
//     }));

//     if (name === "pincode" && value.length === 6) {
//       fetchPinDetails(value);
//     }
//   };

//   // Fetch State / District / Taluka from pincode
//   const fetchPinDetails = async (pincode) => {
//     try {
//       setLoadingPin(true);
//       const res = await axios.get(
//         `https://goelectronix.in/public/api/v1/pincode/${pincode}`
//       );

//       if (res.data?.success) {
//         setForm((prev) => ({
//           ...prev,
//           state: res.data.state,
//           district: res.data.district,
//           taluka: res.data.taluk || "",
//         }));
//       } else {
//         Swal.fire("Invalid Pincode", "Enter a correct pincode.", "error");
//       }
//     } catch {
//       Swal.fire("Error", "Unable to fetch pincode details.", "error");
//     } finally {
//       setLoadingPin(false);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     try {
//       await axios.post("https://your-api.com/agent", form);
//       Swal.fire("Success", "Agent added successfully!", "success").then(() =>
//         navigate("/agents")
//       );
//     } catch (err) {
//       Swal.fire("Error", "Failed to save agent.", "error");
//     }
//   };

//   return (
//     <div className="mx-auto bg-white p-6 rounded-xl shadow-sm mt-4 mb-10">
//       <h2 className="text-xl font-semibold text-[#0A3E91] mb-4">
//         Add New Agent
//       </h2>

//       <form onSubmit={handleSubmit} className="space-y-4">
        
//         {/* PERSONAL DETAILS */}
//         <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
//           <input
//             name="first_name"
//             value={form.first_name}
//             onChange={handleChange}
//             placeholder="Authorise Person First Name"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="middle_name"
//             value={form.middle_name}
//             onChange={handleChange}
//             placeholder="Middle Name"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="last_name"
//             value={form.last_name}
//             onChange={handleChange}
//             placeholder="Last Name"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="phone_no"
//             value={form.phone_no}
//             onChange={handleChange}
//             placeholder="Phone Number"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="official_email"
//             value={form.official_email}
//             onChange={handleChange}
//             placeholder="Official Email ID"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="position"
//             value={form.position}
//             onChange={handleChange}
//             placeholder="Position"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />
//         </div>

//         {/* ADDRESS BLOCK */}
//         <h3 className="text-md font-semibold text-gray-700 mt-4">Address</h3>

//         <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
//           <input
//             name="address1"
//             value={form.address1}
//             onChange={handleChange}
//             placeholder="Address Line 1"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="address2"
//             value={form.address2}
//             onChange={handleChange}
//             placeholder="Address Line 2"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="pincode"
//             value={form.pincode}
//             maxLength={6}
//             onChange={handleChange}
//             placeholder="Pincode"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />

//           {loadingPin && <p className="text-xs text-blue-600">Fetching...</p>}

//           <input
//             name="state"
//             value={form.state}
//             readOnly
//             placeholder="State"
//             className="border rounded-lg px-3 py-2 text-sm w-full bg-gray-100"
//           />

//           <input
//             name="district"
//             value={form.district}
//             readOnly
//             placeholder="District"
//             className="border rounded-lg px-3 py-2 text-sm w-full bg-gray-100"
//           />

//           <input
//             name="taluka"
//             value={form.taluka}
//             readOnly
//             placeholder="Taluka"
//             className="border rounded-lg px-3 py-2 text-sm w-full bg-gray-100"
//           />

//           <input
//             name="location"
//             value={form.location}
//             onChange={handleChange}
//             placeholder="Location"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />
//         </div>

//         {/* BUSINESS DETAILS */}
//         <h3 className="text-md font-semibold text-gray-700 mt-4">
//           Business Details
//         </h3>

//         <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
//           <input
//             name="business_name"
//             value={form.business_name}
//             onChange={handleChange}
//             placeholder="Business Name"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="owner_first"
//             value={form.owner_first}
//             onChange={handleChange}
//             placeholder="Owner First Name"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="owner_middle"
//             value={form.owner_middle}
//             onChange={handleChange}
//             placeholder="Owner Middle Name"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="owner_last"
//             value={form.owner_last}
//             onChange={handleChange}
//             placeholder="Owner Last Name"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="owner_contact"
//             value={form.owner_contact}
//             onChange={handleChange}
//             placeholder="Owner Contact Number"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="owner_email"
//             value={form.owner_email}
//             onChange={handleChange}
//             placeholder="Owner Email ID"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="pan_no"
//             value={form.pan_no}
//             onChange={handleChange}
//             placeholder="PAN Number (Unique)"
//             className="border rounded-lg px-3 py-2 text-sm w-full"
//           />
//         </div>

//         {/* BUTTONS */}
//         <div className="flex justify-end gap-3 mt-5">
//           <BlueButton type="button" onClick={() => navigate("/agents")}>
//             Cancel
//           </BlueButton>
//           <BlueButton type="submit">Save Agent</BlueButton>
//         </div>
//       </form>
//     </div>
//   );
// }
